import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  useOrgGroupsQuery,
  useOrgRolesQuery,
} from "~/components/Admin/Group/Groups.generated";
import ChecklistFilterDropdown from "~/components/Checklist/ChecklistFilterDropdown/ChecklistFilterDropdown";
import { PrimaryButton, SecondaryButton } from "~/components/common/buttons";
import { Toolbar } from "~/components/common/toolbars";
import Dialog from "~/components/generic/Dialog";
import { DropdownMenuAlign } from "~/components/menus/DropdownMenu";
import {
  FundEntityOverviewFragment,
  SubpageOverviewFragment,
} from "~/components/Overview/OverviewPage.generated";
import { useUpdateSubpageMutation } from "~/components/Overview/Subpage.generated";
import { gettext } from "~/utils/text";

type Item = {
  key: string;
  value: string;
};

interface RenderDropdownItemProps {
  item: Item;
  selected: boolean;
}

export interface UpdateSubpageDialogProps {
  isVisible: boolean;
  orgSlug: string;
  subpage: SubpageOverviewFragment;
  subject: FundEntityOverviewFragment;
  onClose: () => void;
  loading: boolean;
  error?: string;
}

export const UpdateSubpageDialog = ({
  isVisible,
  orgSlug,
  subpage,
  subject,
  onClose,
  loading,
  error,
}: UpdateSubpageDialogProps) => {
  const isRolePermission = subject.subpagePermissions === "ROLE";
  const { data: rolesData, loading: rolesLoading } = useOrgRolesQuery({
    variables: { orgSlug: orgSlug },
  });
  const { data: groupsData, loading: groupsLoading } = useOrgGroupsQuery({
    variables: { orgSlug: orgSlug },
  });
  const [updateSubpage] = useUpdateSubpageMutation();
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    isRolePermission
      ? subpage.roles?.map((role) => role.id) || []
      : subpage.groups?.map((group) => group.id) || []
  );
  const [isRestricted, setIsRestricted] = useState<boolean>(subpage.restricted);
  const [title, setTitle] = useState<string>(subpage.title); // New state for title

  // Reset state when the subpage changes
  useEffect(() => {
    setSelectedPermissions(
      isRolePermission
        ? subpage.roles?.map((role) => role.id) || []
        : subpage.groups?.map((group) => group.id) || []
    );
    setIsRestricted(subpage.restricted);
    setTitle(subpage.title); // Reset title when subpage changes
  }, [subpage, isRolePermission]);

  const cancel = () => {
    onClose();
  };

  const save = async () => {
    const variables: {
      id: string;
      subjectType: "HoldingCompany" | "Portfolio";
      restricted: boolean;
      title: string; // Include title in variables
      roles?: string[]; // Optional roles property
      groups?: string[]; // Optional groups property
    } = {
      id: subpage.id,
      subjectType: subject.__typename as "HoldingCompany" | "Portfolio",
      restricted: isRestricted,
      title: title, // Add title to mutation variables
    };

    if (isRolePermission) {
      variables.roles = selectedPermissions;
    } else {
      variables.groups = selectedPermissions;
    }

    try {
      await updateSubpage({
        variables: variables,
        refetchQueries: ["PortfolioPage", "HoldingCompanyPage"],
      });
      // Reset state and close modal
      onClose();
    } catch (error) {
      console.error("Error updating subpage:", error);
    }
  };

  const permissionsList: { id: string; name: string }[] = isRolePermission
    ? (rolesData?.roles || [])
        .filter((role) => role.name) // Filter out roles with null/undefined names
        .map((role) => ({
          id: role.id,
          name: role.name as string, // Assert that name is now a string
        }))
    : (groupsData?.groupPages?.groups || [])
        .filter((group) => group.name) // Filter out groups with null/undefined names
        .map((group) => ({
          id: group.id,
          name: group.name as string, // Assert that name is now a string
        }));

  const renderDropdownItem = ({ item, selected }: RenderDropdownItemProps) => [
    <span key="ok" className="filter-dropdown__selected">
      {selected && <i className="icon icon--ok-blue" />}
    </span>,
    item.value,
  ];

  const getButtonText = (
    items: { key: string; value: string }[],
    selectedItems: string[]
  ) => {
    if (selectedItems.length === 0) {
      return gettext("Internal");
    } else if (selectedItems.length === items.length) {
      return gettext("All");
    } else if (selectedItems.length === 1) {
      return items.find((item) => item.key === selectedItems[0])?.value;
    } else {
      return gettext("Multiple");
    }
  };

  return (
    <CustomDialog
      isOpen={isVisible}
      header={`Update Subpage "${subpage.title}" Permissions`}
      onRequestClose={cancel}
      actions={
        <Toolbar>
          <SecondaryButton onClick={cancel} disabled={loading}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={loading}>
            Save
          </PrimaryButton>
        </Toolbar>
      }
    >
      {error && <div className="alert alert-danger">{error}</div>}
      <div
        style={{
          flexGrow: 1, // Allow the div to grow and fill available height
          overflowY: "auto",
        }}
      >
        {/* Title Input */}
        <div className="form-group">
          <label htmlFor="subpage-title">Title</label>
          <input
            id="subpage-title"
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={isRestricted}
              onChange={(e) => setIsRestricted(e.target.checked)}
              disabled={loading}
            />{" "}
            Restrict access
          </label>
          <p style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}>
            By default all users with access to the overview page will see this
            subpage. Selecting "Restrict access" will allow you to specify which
            roles or groups can see this subpage.
          </p>
        </div>
        <ChecklistFilterDropdown
          getButtonText={getButtonText}
          items={permissionsList.map((permission) => ({
            key: permission.id,
            value: permission.name,
          }))}
          label={isRolePermission ? "Roles" : "Groups"}
          title={`Filter by ${isRolePermission ? "Roles" : "Groups"}`}
          selectedOptions={selectedPermissions}
          onSelected={(selected) => setSelectedPermissions(selected)}
          multiple
          disabled={!isRestricted || loading || rolesLoading || groupsLoading}
          menuAlign={DropdownMenuAlign.LEFT}
        >
          {renderDropdownItem}
        </ChecklistFilterDropdown>
      </div>
    </CustomDialog>
  );
};

const CustomDialog = styled(Dialog)`
  & .ReactModal__Content {
    height: calc(100vh - 200px); /* Full viewport height minus a 20px buffer */
    max-height: calc(100vh - 200px); /* Prevent overflow with a 20px buffer */
    overflow-y: auto; /* Enable scrolling if content exceeds height */
    display: flex;
    flex-direction: column;
    margin-bottom: 10px; /* Add a margin around the dialog */
  }
`;
